import { useEffect, useState } from 'react';
import axios from 'axios';
import { setNodeRedBaseURL } from '../services/axios';
import { NODE_RED_OFFLINE_URL } from '../env';

const HEALTH_INTERVAL_MS = 5000;  // 5s
const HEALTH_TIMEOUT_MS = 1500;   // 1.5s

async function isHealthy(url) {
  if (!url) return false;
  const cleanUrl = url.replace(/\/+$/, '');
  const res = await axios.get(`${cleanUrl}/health`, { timeout: HEALTH_TIMEOUT_MS });
  return res.status === 200;
}

export function useNodeRedWatcher() {
  const [backendUnavailable, setBackendUnavailable] = useState(false);

  // ✅ usados pelo Home (online prompt + banner de “voltou pro online”, se você quiser)
  const [suggestOnlineLogin, setSuggestOnlineLogin] = useState(false);
  const [autoSwitchedBackOnline, setAutoSwitchedBackOnline] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function checkBackends() {
      if (cancelled) return;

      const nodeRedOnlineUrl = sessionStorage.getItem('nodeRedOnlineUrl') || null;
      const offlineUrl = NODE_RED_OFFLINE_URL;

      const prevMode = sessionStorage.getItem('nodeRedMode'); // 'online' | 'offline' | 'unavailable' | null

      // 1) tenta online (se existir)
      if (nodeRedOnlineUrl) {
        try {
          const okOnline = await isHealthy(nodeRedOnlineUrl);
          if (cancelled) return;

          if (okOnline) {
            setNodeRedBaseURL(nodeRedOnlineUrl);
            sessionStorage.setItem('nodeRedMode', 'online');
            setBackendUnavailable(false);

            // se antes estava offline/unavailable, sinaliza que voltou pro online
            if (prevMode && prevMode !== 'online') setAutoSwitchedBackOnline(true);

            // se você quiser sugerir login online quando existir URL online:
            // (você pode ajustar a regra; aqui eu só sugiro se tiver URL online salvo)
            setSuggestOnlineLogin(true);

            return;
          }
        } catch {
          console.warn('Node-RED online não respondeu, tentando offline...');
        }
      }

      // 2) tenta offline
      try {
        const okOffline = await isHealthy(offlineUrl);
        if (cancelled) return;

        if (okOffline) {
          setNodeRedBaseURL(offlineUrl);
          sessionStorage.setItem('nodeRedMode', 'offline');
          setBackendUnavailable(false);

          // offline ok → não faz sentido sugerir login online (a não ser que você queira)
          setSuggestOnlineLogin(false);

          return;
        }
      } catch (e) {
        console.warn('Node-RED offline também não respondeu.', e?.message);
      }

      // 3) nenhum respondeu
      if (!cancelled) {
        sessionStorage.setItem('nodeRedMode', 'unavailable');
        setBackendUnavailable(true);
        setSuggestOnlineLogin(false);
      }
    }

    checkBackends();
    const id = setInterval(checkBackends, HEALTH_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  // ✅ funções que o Home está tentando chamar
  const clearSuggestion = () => setSuggestOnlineLogin(false);
  const clearAutoSwitchedFlag = () => setAutoSwitchedBackOnline(false);

  return {
    backendUnavailable,
    suggestOnlineLogin,
    autoSwitchedBackOnline,
    clearSuggestion,
    clearAutoSwitchedFlag,
  };
}